import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

import cn from 'classnames'
import moment from 'moment'

import FDAccordion from 'components/Shared/FDAccordion'
import { Table } from 'react-bootstrap'
import Paginator from 'components/Shared/Paginator'
import { useTranslation } from 'react-i18next'
import LoaderSpinner from 'components/Shared/Loader/LoaderSpinner'
import TableMessage from 'components/Shared/TableMessage'
import { fetchRecommendedEventsForCustomer } from 'api/affinity'
import { LinkIcon } from 'images'
import { Link } from 'react-router-dom'

const RecommendedEvents = ({ customerId }) => {
  const [activeKeys, setActiveKeys] = useState([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [recommendedEvents, setRecommendedEvents] = useState([])
  const pageSize = 10
  const totalPages = Math.ceil(recommendedEvents?.length / pageSize)

  useEffect(() => {
    async function fetchData() {  
      setLoading(true)
      setError(false)
      const response = await fetchRecommendedEventsForCustomer(customerId)
      if (response.success) {
        setRecommendedEvents(response.data)
      } else {
        setRecommendedEvents([])
        setError(true)
      }
      setLoading(false)
    }
    fetchData()
  }, [])

  const currentPage = useMemo(() => {
    const start = (page - 1) * pageSize
    const end = start + pageSize
    return recommendedEvents?.slice(start, end)
  }, [page, recommendedEvents])

  const { t } = useTranslation()

  return (
    <div className={cn('customer-details__recommended-events', { open: activeKeys.length > 0 })}>
      <FDAccordion defaultActiveKeys={activeKeys} onChange={setActiveKeys}>
        <FDAccordion.Item key={0}>
          <FDAccordion.Toggle>
            <div className="d-flex justify-content-between align-items-center">
              <div>{t('Sentinel.results.customerDetails.recommendedEvents.title')}</div>
              <div className={cn('accordion-arrow', { open: activeKeys.length > 0 })} />
            </div>
          </FDAccordion.Toggle>
          <FDAccordion.Body>
            <div>
              {recommendedEvents?.length === 0 && !loading && !error && (
                <TableMessage 
                  variant="empty" 
                  description={t('Sentinel.results.customerDetails.recommendedEvents.empty.description')}
                />
              )}
              {loading && (
                <LoaderSpinner 
                  text={t('Sentinel.results.customerDetails.recommendedEvents.loading')} 
                  className="m-2 p-2"
                />
              )}
              {error && !loading && (
                <TableMessage 
                  variant="error" 
                  description={t('Sentinel.results.customerDetails.recommendedEvents.error.title')}
                />
              )}
              {recommendedEvents?.length > 0 && !loading && !error && (
                <Table responsive>
                  <colgroup>
                    <col style={{ width: '35%', minWidth: '120px' }} />
                    <col style={{ width: '20%', minWidth: '120px' }} />
                    <col style={{ width: '25%', minWidth: '120px' }} />
                    <col style={{ width: '20%', minWidth: '120px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t('Sentinel.results.customerDetails.recommendedEvents.eventTitle')}</th>
                      <th>{t('Sentinel.results.customerDetails.recommendedEvents.date')}</th>
                      <th>{t('Sentinel.results.customerDetails.recommendedEvents.location')}</th>
                      <th>{t('Sentinel.results.customerDetails.recommendedEvents.category')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPage?.map((event, index) => (
                      <tr key={index}>
                        <td>
                          <Link to={`/events?drawer-open=true&event-id=${event.id}`} className="event-title">
                            <span>{event.title}</span>

                            <LinkIcon width={16} height={16} />
                          </Link>
                        </td>
                        <td>
                          <time dateTime={moment.utc(event.start_date_time).format('DD.MM.YYYY, HH:mm')}>
                            {moment.utc(event.start_date_time).format('DD.MM.YYYY, HH:mm')}
                          </time>
                        </td>
                        <td>{event.venue_name},  {event.city}</td>
                        <td>{event.category ? t(`Simulation.form.eventCategoryOptions.${event.category}`) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            {!loading && !error && <Paginator
              onPageClicked={setPage}
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={recommendedEvents?.length}
              hideWhenSinglePage
              hidePageSize
            />}
          </FDAccordion.Body>
        </FDAccordion.Item>
      </FDAccordion>
    </div>
  )
}

RecommendedEvents.propTypes = {
  customerId: PropTypes.string.isRequired,
}

export default RecommendedEvents